import { ArrowRight, Briefcase, Building2 } from "lucide-react";
import { AUDIENCES } from "./workflowSteps";

const ICONS = { customer: Building2, reseller: Briefcase };

// Where Room Planner starts: who's planning the room decides which steps follow and
// what the report holds. Shown in the same kind of card as the steps, the two paths side
// by side where there's room.
// `stacked` keeps the two paths one above the other: the intro hero puts this in a
// narrow right-hand rail, where the side-by-side grid would squeeze both cards.
//
// `footer` renders inside this panel rather than below it. In the hero this panel is
// the right half of a two-panel composition and has to match the stage opposite it, so
// anything that belongs to it lives within its border — a second floating element below
// would break that symmetry, and wrapping this in an outer panel would nest one card in
// another.
export function AudiencePicker({ audience, onChoose, stacked = false, footer = null }) {
  return (
    <section
      aria-labelledby="rc-start-title"
      className="rc-step-enter flex flex-col rounded-3xl border rc-border rc-fill shadow-sm lg:h-full lg:min-h-0"
    >
      <header className="px-5 pt-5">
        <p className="rc-label text-[color:var(--rc-accent-ink)]">Get started</p>
        <h2 id="rc-start-title" className="rc-title mt-1.5 rc-ink">
          Who's planning this room?
        </h2>
        {/* Carries the "free, no sign-up" signal the deleted kicker used to hold. Here it
            answers the question a visitor actually has at the moment they choose. */}
        <p className="rc-body mt-1.5 rc-ink-muted">
          Free, no sign-up. Pick the path that fits you — you can switch at any time.
        </p>
      </header>
      {/* The options take the panel's slack and centre in it, so a taller panel grows
          the breathing room around the choice instead of leaving a gap beneath it. */}
      <div
        role="radiogroup"
        aria-labelledby="rc-start-title"
        className={`flex flex-col gap-2.5 px-5 pb-5 pt-4 lg:min-h-0 lg:flex-1 lg:justify-center lg:overflow-y-auto ${
          stacked ? "" : "md:grid md:grid-cols-2"
        }`}
      >
        {Object.entries(AUDIENCES).map(([id, a]) => {
          const Icon = ICONS[id];
          const active = audience === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChoose(id)}
              className={`group flex items-start gap-3.5 rounded-xl border p-4 text-left transition-[background-color,border-color,box-shadow,transform] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 active:scale-[0.99] ${
                active
                  ? "border-blue-500 bg-blue-500/10 shadow-[0_0_0_1px_rgba(59,130,246,1)]"
                  : "rc-border rc-fill hover:border-blue-400/40 hover:bg-blue-500/10 hover:shadow-sm"
              }`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="rc-label block text-[color:var(--rc-accent-ink)]">{a.label}</span>
                <span className="rc-subtitle mt-1 block rc-ink">{a.title}</span>
                <span className="rc-body mt-1.5 block rc-ink-muted">{a.description}</span>
              </span>
              <ArrowRight
                aria-hidden="true"
                className="mt-2.5 h-4 w-4 shrink-0 rc-ink-muted transition-transform duration-200 ease-out-strong group-hover:translate-x-0.5 group-hover:text-[color:var(--rc-accent)] motion-reduce:transition-none"
              />
            </button>
          );
        })}
      </div>
      {footer && <div className="border-t rc-border px-5 py-4">{footer}</div>}
    </section>
  );
}
